class DiscountField extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('.discount-field__input');
    this.applyBtn = this.querySelector('.discount-field__apply');
    this.appliedRow = this.querySelector('.discount-field__applied');
    this.appliedValue = this.querySelector('.discount-field__applied-value');
    this.errorEl = this.querySelector('.discount-field__error');

    this.subtotalEl = this.closest('cart-drawer')?.querySelector('.cart-drawer__subtotal');

    this.onFocus = () => this.lockViewport();
    this.onBlur = () => this.unlockViewport();
    this.onVisualViewport = () => this.syncVisualViewport();

    this.bindEvents();
    this.listen();
    this.refresh();
  }

  disconnectedCallback() {
    this.input?.removeEventListener('focus', this.onFocus);
    this.input?.removeEventListener('blur', this.onBlur);
    this.unlockViewport();
  }

  bindEvents() {
    this.applyBtn?.addEventListener('click', () => this.applyDiscount());

    this.input?.addEventListener('focus', this.onFocus);
    this.input?.addEventListener('blur', this.onBlur);

    this.input?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.applyDiscount();
      }
    });
  }

  isMobileField() {
    return window.matchMedia('(pointer: coarse), (max-width: 767px)').matches;
  }

  lockViewport() {
    if (!this.isMobileField()) return;

    const meta = document.querySelector('meta[name="viewport"]');
    if (meta && !this._viewportLocked) {
      this._viewportOriginal = meta.getAttribute('content') || 'width=device-width, initial-scale=1';
      const cleaned = this._viewportOriginal
        .replace(/,?\s*(maximum-scale|minimum-scale|user-scalable)\s*=\s*[^,]+/gi, '')
        .replace(/,,+/g, ',')
        .replace(/^,|,$/g, '')
        .trim();
      meta.setAttribute('content', `${cleaned}, maximum-scale=1`);
      this._viewportLocked = true;
    }

    this.closest('cart-drawer')?.classList.add('is-keyboard-open');

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.onVisualViewport);
      window.visualViewport.addEventListener('scroll', this.onVisualViewport);
    }

    this.syncVisualViewport();
  }

  unlockViewport() {
    const meta = document.querySelector('meta[name="viewport"]');
    if (meta && this._viewportLocked && this._viewportOriginal) {
      meta.setAttribute('content', this._viewportOriginal);
    }
    this._viewportLocked = false;

    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.onVisualViewport);
      window.visualViewport.removeEventListener('scroll', this.onVisualViewport);
    }

    const drawer = this.closest('cart-drawer');
    drawer?.classList.remove('is-keyboard-open');
    drawer?.style.removeProperty('--cart-vv-height');
    drawer?.style.removeProperty('--cart-vv-offset');
  }

  syncVisualViewport() {
    const vv = window.visualViewport;
    const drawer = this.closest('cart-drawer');
    if (!vv || !drawer) return;

    drawer.style.setProperty('--cart-vv-height', `${Math.round(vv.height)}px`);
    drawer.style.setProperty('--cart-vv-offset', `${Math.round(vv.offsetTop)}px`);
  }

  listen() {
    document.addEventListener('cart:updated', () => {
      setTimeout(() => this.refresh(), 300);
    });
  }

  async applyDiscount() {
    const code = this.input?.value.trim();

    if (!code || this._applying) return;

    this._applying = true;
    this.setLoading(true);
    this.hideError();

    try {
      const before = await fetch('/cart.js').then(r => r.json());

      const updateRes = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: code })
      });

      const cart = updateRes.ok
        ? await updateRes.json()
        : await fetch('/cart.js').then(r => r.json());

      if (this.discountWasAccepted(before, cart, code)) {
        this.updateUI(cart, code);
        document.dispatchEvent(new CustomEvent('cart:updated'));
      } else {
        this.showError('Enter a valid discount code');
        this.updateAppliedRow(null);
        this.updateSubtotal(cart);
      }

    } catch (e) {
      console.error('Discount apply error:', e);
      this.showError('Something went wrong. Please try again.');
    } finally {
      this._applying = false;
      this.setLoading(false);
    }
  }

  async refresh() {
    try {
      const cart = await fetch('/cart.js').then(r => r.json());
      this.updateUI(cart);
    } catch (e) {
      console.error('Discount field refresh error:', e);
    }
  }

  updateUI(cart, enteredCode) {
    const discount = this.getDiscountInfo(cart);

    if (discount) {
      this.hideError();
      if (this.input && enteredCode) this.input.value = enteredCode;
      else if (this.input && discount.code) this.input.value = discount.code;
      this.updateAppliedRow(discount.percent);
    } else {
      this.updateAppliedRow(null);
    }

    this.updateSubtotal(cart);
  }

  getDiscountInfo(cart) {
    const applications = cart.cart_level_discount_applications || [];

    if (!applications.length && cart.total_discount <= 0) return null;

    const app = applications[0];

    if (app) {
      const percent = app.value_type === 'percentage'
        ? Math.round(parseFloat(app.value))
        : this.calcDiscountPercent(cart);

      return {
        code: app.title,
        percent
      };
    }

    const percent = this.calcDiscountPercent(cart);

    if (percent <= 0) return null;

    return { code: '', percent };
  }

  calcDiscountPercent(cart) {
    if (!cart.original_total_price || cart.total_discount <= 0) return 0;

    return Math.round(
      (cart.total_discount / cart.original_total_price) * 100
    );
  }

  discountLabels(cart) {
    const labels = [];

    for (const entry of cart.discount_codes || []) {
      if (entry && entry.applicable === false) continue;
      labels.push(entry.code || entry);
    }

    for (const app of cart.cart_level_discount_applications || []) {
      labels.push(app.title, app.code);
    }

    for (const item of cart.items || []) {
      for (const alloc of item.line_level_discount_allocations || []) {
        const app = alloc.discount_application || {};
        labels.push(app.title, app.code);
      }
      for (const disc of item.discounts || []) {
        labels.push(disc.title, disc.code);
      }
    }

    return labels
      .map((value) => String(value || '').trim().toLowerCase())
      .filter(Boolean);
  }

  isDiscountApplied(cart, code) {
    const wanted = String(code || '').trim().toLowerCase();
    if (!wanted) return false;
    return this.discountLabels(cart).includes(wanted);
  }

  discountWasAccepted(before, after, code) {
    if (this.isDiscountApplied(after, code)) return true;

    const beforeCount = this.discountLabels(before).length;
    const afterCount = this.discountLabels(after).length;
    if (afterCount > beforeCount) return true;

    return (after.total_discount || 0) > (before.total_discount || 0);
  }

  updateAppliedRow(percent) {
    if (percent == null) {
      this.appliedRow.hidden = true;
      this.appliedValue.textContent = '';
      return;
    }

    this.appliedValue.textContent = percent > 0 ? `${percent}%` : '';
    this.appliedRow.hidden = false;
  }

  updateSubtotal(cart) {
    if (!this.subtotalEl) return;

    const hasDiscount = cart.total_discount > 0;

    if (hasDiscount) {
      this.subtotalEl.innerHTML = `
        <span class="cart-drawer__subtotal--original">${this.formatMoney(cart.original_total_price)}</span>
        <span class="cart-drawer__subtotal--discounted">${this.formatMoney(cart.total_price)}</span>
      `;
    } else {
      this.subtotalEl.textContent = this.formatMoney(cart.total_price);
    }
  }

  formatMoney(cents) {
    return `$${(cents / 100).toFixed(2)}`;
  }

  setLoading(state) {
    this.classList.toggle('is-loading', state);
    if (this.applyBtn) this.applyBtn.disabled = state;
  }

  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
  }

  hideError() {
    if (!this.errorEl) return;
    this.errorEl.hidden = true;
    this.errorEl.textContent = '';
  }
}

customElements.define('discount-field', DiscountField);
